const SELECTORS = {
  root: "[data-order-lookup]",
  form: "[data-order-lookup-form]",
  feedback: "[data-order-lookup-feedback]",
  results: "[data-order-lookup-results]",
  name: "[data-order-lookup-name]",
  placed: "[data-order-lookup-placed]",
  payment: "[data-order-lookup-financial-status]",
  fulfillment: "[data-order-lookup-fulfillment-status]",
  items: "[data-order-lookup-line-items]",
  tracking: "[data-order-lookup-tracking]",
};

export function formatOrderDate(value, locale = "en-US") {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Placed date unavailable";
  return `Placed on ${new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date)}`;
}

export function buildItemLabel(item) {
  const quantity = Math.max(0, Number(item?.quantity) || 0);
  return quantity > 1 ? `${item?.name || "Item"} x ${quantity}` : item?.name || "Item";
}

export function normalizeOrderDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

export function initializeOrderLookup(root) {
  if (!root || root.dataset.orderLookupReady === "true") return;

  const form = root.querySelector(SELECTORS.form);
  const feedback = root.querySelector(SELECTORS.feedback);
  const results = root.querySelector(SELECTORS.results);
  const button = form?.querySelector('[type="submit"]');
  const orderNumberInput = form?.elements.namedItem("order_number");
  if (!form || !feedback || !results || !button || !(orderNumberInput instanceof HTMLInputElement)) return;

  root.dataset.orderLookupReady = "true";
  feedback.setAttribute("role", "status");
  feedback.setAttribute("aria-live", "polite");

  orderNumberInput.addEventListener("input", () => {
    orderNumberInput.value = normalizeOrderDigits(orderNumberInput.value);
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearOutput(feedback, results);

    const data = new FormData(form);
    const orderDigits = normalizeOrderDigits(data.get("order_number"));
    if (!orderDigits) {
      showFeedback(feedback, "Please enter your order number.", true);
      orderNumberInput.focus();
      return;
    }

    const turnstileToken = String(data.get("cf-turnstile-response") || "");
    if (!turnstileToken) {
      showFeedback(feedback, "Please complete the security check.", true);
      return;
    }

    const originalLabel = root.dataset.buttonLabel || button.textContent || "Look Up Order";
    setLoading(button, true);

    try {
      const response = await fetch(root.dataset.endpoint || "/apps/order-lookup", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          order_number: `AGB-${orderDigits}`,
          email: String(data.get("email") || ""),
          turnstile_token: turnstileToken,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok || !payload.order) {
        throw new LookupError(payload?.message || "We could not look up that order right now.");
      }

      renderOrder(root, payload.order);
      results.hidden = false;
      showFeedback(feedback, "Order found.", false);
    } catch (error) {
      const message = error instanceof LookupError
        ? error.message
        : "We could not look up that order right now. Please try again shortly.";
      showFeedback(feedback, message, true);
    } finally {
      setLoading(button, false, originalLabel);
      window.turnstile?.reset();
    }
  });
}

export function renderOrder(root, order) {
  setText(root, SELECTORS.name, `Order ${order.number || ""}`.trim());
  setText(root, SELECTORS.placed, formatOrderDate(order.placed_on));
  setStatus(root, SELECTORS.payment, "Payment", order.payment_status);
  setStatus(root, SELECTORS.fulfillment, "Fulfillment", order.fulfillment_status);

  const items = root.querySelector(SELECTORS.items);
  replaceChildren(items, (order.items || []).map((item) => {
    const row = document.createElement("article");
    row.className = "order-lookup-line-item";

    if (item.image_url) {
      const image = document.createElement("img");
      image.className = "order-lookup-line-item-image";
      image.src = item.image_url;
      image.alt = item.image_alt || item.name || "Order item";
      image.loading = "lazy";
      image.width = 72;
      image.height = 72;
      row.append(image);
    }

    const copy = document.createElement("div");
    copy.className = "order-lookup-line-item-copy";

    const title = document.createElement("p");
    title.className = "order-lookup-line-item-title";
    title.textContent = buildItemLabel(item);
    copy.append(title);

    if (item.sku) {
      const sku = document.createElement("p");
      sku.className = "order-lookup-line-item-sku";
      sku.textContent = `SKU: ${item.sku}`;
      copy.append(sku);
    }

    row.append(copy);
    return row;
  }));

  const tracking = root.querySelector(SELECTORS.tracking);
  const trackingRows = (order.tracking || []).map((entry) => {
    const row = document.createElement("p");
    const prefix = [entry.carrier, entry.number].filter(Boolean).join(": ");
    if (entry.url) {
      const link = document.createElement("a");
      link.href = entry.url;
      link.rel = "noopener noreferrer";
      link.target = "_blank";
      link.textContent = prefix || "Track shipment";
      row.append(link);
    } else {
      row.textContent = prefix;
    }
    return row;
  });

  if (!trackingRows.length) {
    const pending = document.createElement("p");
    pending.textContent = "Tracking details will appear here once your order ships.";
    trackingRows.push(pending);
  }
  replaceChildren(tracking, trackingRows);

  hideUnusedLegacyFields(root);
}

function setText(root, selector, value) {
  const element = root.querySelector(selector);
  if (element) element.textContent = value;
}

function setStatus(root, selector, label, value) {
  const element = root.querySelector(selector);
  if (!element) return;
  const status = String(value || "Unknown");
  element.textContent = `${label}: ${status}`;
  element.dataset.tone = statusTone(status);
}

export function statusTone(value) {
  const status = String(value || "").trim().toLowerCase();
  if (/cancelled|canceled/.test(status)) return "danger";
  if (/^(paid|fulfilled)$/.test(status)) return "success";
  if (/unfulfilled|partial|pending|authorized|on hold|in progress/.test(status)) return "warning";
  return "neutral";
}

function replaceChildren(container, children) {
  if (container) container.replaceChildren(...children);
}

function clearOutput(feedback, results) {
  feedback.hidden = true;
  feedback.textContent = "";
  feedback.removeAttribute("data-state");
  results.hidden = true;
}

function showFeedback(feedback, message, isError) {
  feedback.textContent = message;
  feedback.dataset.state = isError ? "error" : "success";
  feedback.hidden = false;
}

function setLoading(button, loading, label = "Look Up Order") {
  button.disabled = loading;
  button.setAttribute("aria-busy", String(loading));
  button.textContent = loading ? "Looking up..." : label;
}

function hideUnusedLegacyFields(root) {
  [
    "[data-order-lookup-status-link]",
    "[data-order-lookup-shipping-address]",
    "[data-order-lookup-totals]",
  ].forEach((selector) => {
    const element = root.querySelector(selector);
    if (element) element.hidden = true;
  });
}

class LookupError extends Error {}

function initializeAll(scope = document) {
  scope.querySelectorAll(SELECTORS.root).forEach(initializeOrderLookup);
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
}
